import { FC, useEffect, useState } from 'react';
import { FORM_ERROR } from 'final-form';
import Spacing from 'components/Spacing';
import { CheckoutForm } from './CheckoutForm';
import { MainContainer } from 'screens/Main/styled';
import { CheckoutContainer } from 'screens/Checkout/styled';
import { getChariotConnectId } from 'components/Slate/utils';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { ContributionRules } from 'components/Slate/ContributionRules';
import { useAuth } from 'hooks/auth';
import { getUserProfile } from 'store/user/actions';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getUserData } from 'selectors/user';
import { getUrlParams } from 'helpers/utils';
import { postPayDaf } from 'store/pay/actions';

interface Props {
  candidates: CandidateType[];
  clientId: string;
  pageId: number;
  donationAmount: number;
}

type ChariotSuccessDetails = {
  amount: number;
  trackingId: string;
  workflowSessionId: string;
};

export const DAFCheckout: FC<Props> = ({ clientId, pageId, donationAmount, candidates }) => {
  const dispatch = useAppDispatch();
  const { urlRef, urlPartner } = getUrlParams();
  const [isDataReady, setIsDataReady] = useState(false);
  const [chariotSuccessDetails, setChariotSuccessDetails] = useState<
    ChariotSuccessDetails | undefined
  >(undefined);
  const user = useAppSelector(getUserData);

  const chariotConnectId = getChariotConnectId(candidates);

  useAuth(() => {
    dispatch(getUserProfile());
  });

  useEffect(() => {
    if (chariotSuccessDetails) {
      setIsDataReady(true);
    }
  }, [chariotSuccessDetails]);

  const getOnSubmit =
    (chariotSuccessDetails?: ChariotSuccessDetails) => async (formValues: any) => {
      if (!chariotSuccessDetails) {
        return new Promise(resolve => {
          resolve({
            [FORM_ERROR]: 'Something went wrong. Please try again.',
          });
        });
      }
      const body = {
        payment_method: 'daf',
        donor_last_name: formValues.last_name,
        donor_first_name: formValues.first_name,
        ref_code: urlRef,
        donor_email: formValues.email,
        client_id: clientId.toString(),
        page_id: pageId,
        amount: chariotSuccessDetails.amount,
        chariot_grant_id: chariotSuccessDetails.trackingId,
        page: urlPartner,
        processing_fee: 0,
        source_code: 'chariot',
        recipient: candidates[0].recipient_name || '',
        recipient_id: candidates[0].recipient_id,
        workflow_session_id: chariotSuccessDetails.workflowSessionId,
      };
      return new Promise(resolve => {
        dispatch(postPayDaf(body, !!user?.id))
          .then(response => {
            resolve(response);
          })
          .catch(() => {
            resolve({
              [FORM_ERROR]:
                'Sorry, there was an error processing your donation. Please double check your information and try again.',
            });
          });
      });
    };

  // We need to create a new function for onDonationRequest each time
  // the valid state changes because the ChariotConnect
  // component only updates when the onDonationRequest prop changes and not when
  // onSuccess or disabled props change
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getOnDonationRequest = (valid: boolean) => () => {
    return {
      amount: donationAmount ? donationAmount * 100 : 0,
    };
  };

  const onChariotDonateSuccess = (response: any) => {
    if (response.type === 'CHARIOT_SUCCESS') {
      setChariotSuccessDetails({
        amount: response.detail.grantIntent.amount,
        trackingId: response.detail.grantIntent.trackingId,
        workflowSessionId: response.detail.workflowSessionId,
      });
    }
  };

  return (
    <MainContainer>
      <CheckoutContainer>
        <CheckoutForm
          chariotConnectId={chariotConnectId}
          chariotDetailsReady={isDataReady}
          donationAmount={donationAmount}
          getOnDonationRequest={getOnDonationRequest}
          onChariotDonateSuccess={onChariotDonateSuccess}
          onSubmit={getOnSubmit(chariotSuccessDetails)}
          user={user}
        />
        <Spacing $size={70} />
        <ContributionRules />
        <Spacing $size={48.5} />
      </CheckoutContainer>
    </MainContainer>
  );
};
